import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { ArrowUp, WifiOff, CheckCircle, Loader2, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useAuth } from '../contexts/AuthContext';
import { useChatEncryption } from '../hooks/useChatEncryption';
import { updateChatReference, createMyDid, fetchOnChainDidStatus } from '../utils/did-contract';
import { keyMetadataBytesForDidUpdate } from '../utils/did-key-metadata';
import { useOffline } from '../hooks/useOffline';
import { useToast } from '../hooks/useToast';
import { useSubscription } from '../hooks/useSubscription';
import { getActionableErrorMessage } from '../utils/errorMessages';
import { recordFirstMessageSent, recordFirstReplyReceived } from '../utils/conversionMetrics';
import { analytics } from '../lib/analytics';
import { apiFetch, apiUrl } from '../config/api';
import { useSubscriptionTerminal } from '../contexts/SubscriptionTerminalContext';
import GuestModeLimitModal from './GuestModeLimitModal';
import SafetyModal from './SafetyModal';
import { useCrisisDetection } from '../hooks/useCrisisDetection';
import { SUPPORTED_NETWORK_NAME } from '../config/supportedChain';
import { emitChatState } from '../utils/chatStateEvents';
import { markSaveCompleted, shouldShowOnboarding } from '../utils/onboardingStore';
import {
  clearGuestPromptCount,
  readGuestPromptCount,
  writeGuestPromptCount,
} from '../utils/guestPromptStore';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  encrypted?: boolean;
}

const MAX_MESSAGE_LENGTH = 2000;
/**
 * Fallback for the anonymous (wallet-less) daily message allowance, used only
 * until GET /subscription/plans resolves. The server value
 * (`guestDailyPromptLimit`, single-sourced in the API's subscription-config.ts)
 * always wins — see `guestPromptLimit` in the component.
 *
 * Persisted in localStorage (guestPromptStore) so a hard refresh does not
 * silently reset the client counter; the server enforces the real limit via
 * signed device cookie + IP hard cap, so all of this is UX only.
 */
const GUEST_PROMPT_LIMIT_FALLBACK = 25;

/** Delay after the assistant reply finishes before showing the Save session button. */
const SAVE_SESSION_BUTTON_DELAY_MS = 3000;

const FIRST_MESSAGE_SUGGESTIONS = [
  "I'd like to talk through something I'm experiencing and get your guidance.",
  "I'm reaching out because I need a calm, structured perspective on something personal.",
];

function TypingDots({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-1 px-2 py-1 ${className}`} aria-hidden="true">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  );
}

export default function ChatWidget() {
  const [searchParams] = useSearchParams();
  const { authState, userMode, checkDID } = useAuth();
  const { wallet, signer, provider } = useWallet();
  const { symmetricKey, encryptedKey, loadChat, saveChat, isLoading: encryptionLoading, error: encryptionError } = useChatEncryption();
  const { isOffline } = useOffline();
  const { showToast, ToastContainer } = useToast();
  const {
    fetchPlans,
    fetchSubscription,
    fetchUsage,
    fetchPaymentHistory,
    subscription,
    quotaStatus,
    plans,
    getUsagePercentage,
  } = useSubscription();

  // Guest allowance is authoritative on the server (single-sourced in
  // subscription-config.ts) and returned by GET /subscription/plans. The local
  // constant is only a fallback for the first paint before that resolves.
  const guestPromptLimit = plans?.guestDailyPromptLimit ?? GUEST_PROMPT_LIMIT_FALLBACK;

  // Auto-fetch subscription data when wallet is connected
  const [hasShownInitialTerminal, setHasShownInitialTerminal] = useState(false);

  useEffect(() => {
    if (wallet?.address && authState.isVerified) {
      fetchPlans();
      fetchSubscription();
      fetchUsage();
      fetchPaymentHistory();
    }
  }, [wallet?.address, authState.isVerified, fetchPlans, fetchSubscription, fetchUsage, fetchPaymentHistory]);

  // Show terminal right after wallet connect if user doesn't have an ongoing paid
  // subscription. Compare case-insensitively: plan/status can arrive as Prisma
  // enum strings (e.g. "PRO", "ACTIVE").
  useEffect(() => {
    if (!wallet?.address || !authState.isVerified || hasShownInitialTerminal) return;
    if (!subscription) return;

    const status = String(subscription.status || '').toLowerCase();
    const plan = String(subscription.plan || '').toLowerCase();
    const hasActivePaidSubscription =
      (status === 'active' || status === 'trialing') && plan !== 'free';

    if (!hasActivePaidSubscription) {
      setHasShownInitialTerminal(true);
      openSubscriptionTerminal(false);
    }
  }, [wallet?.address, authState.isVerified, subscription, hasShownInitialTerminal]);

  // Check if withContext query parameter is set
  const withContext = searchParams.get('withContext') === 'true';
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendPhase, setSendPhase] = useState<
    'idle' | 'checking' | 'encrypting' | 'sending' | 'streaming' | 'saving' | 'error'
  >('idle');
  const { openSubscriptionTerminal } = useSubscriptionTerminal();
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [guestPromptCount, setGuestPromptCount] = useState<number>(() => readGuestPromptCount());
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  const [isMockMode, setIsMockMode] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(true);
  // Manual save state: track pending save after AI response
  const [pendingDidSave, setPendingDidSave] = useState<{
    visible: boolean;
    saveResult: 'success' | 'cancelled' | 'error' | null;
  }>({ visible: false, saveResult: null });

  // Track if current chat has been saved to DID
  const [chatIsSaved, setChatIsSaved] = useState(false);
  const [isSavingToDid, setIsSavingToDid] = useState(false);
  const [isCreatingDid, setIsCreatingDid] = useState(false);
  // Token id of a freshly minted DID, shown as a one-time success message until
  // the user proceeds to save the session.
  const [didCreatedTokenId, setDidCreatedTokenId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const concurrencyCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  const isGuest = userMode === 'guest';

  // Track if user has sent at least one message (for concurrency polling optimization)
  const hasSentMessage = guestPromptCount > 0 || (messages.length > 0 && !isGuest);

  // Track if we should show DID creation prompt
  const [showDidCreatePrompt, setShowDidCreatePrompt] = useState(false);

  // Crisis detection & Safety Net
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [crisisHotline, setCrisisHotline] = useState<{
    name: string;
    phone: string;
    website: string;
    availability: string;
  } | null>(null);
  const [detectedLanguage, setDetectedLanguage] = useState('en');

  const {
    checkMessage,
    registerSafetyNet,
    dismissModal,
  } = useCrisisDetection({
    enabled: true,
    onCrisisDetected: (result) => {
      setShowSafetyModal(true);
      setCrisisHotline(result.hotline);
      setDetectedLanguage(result.language || 'en');
    },
  });

  // Wallet verified mid-session: reset guest limit and allow persistence path (load replaces thread).
  useEffect(() => {
    if (authState.isVerified) {
      setGuestPromptCount(0);
      clearGuestPromptCount();
      setShowGuestLimitModal(false);
    }
  }, [authState.isVerified]);

  // Rehydrate the counter on mount so a hard refresh does not silently reset guests' quota.
  // Runs once on mount; wallet-verified case is handled by the effect above.
  useEffect(() => {
    setGuestPromptCount((current) => {
      const stored = readGuestPromptCount();
      return stored > current ? stored : current;
    });
  }, []);

  // Show DID creation prompt only after check completes and no token exists for this wallet.
  useEffect(() => {
    if (authState.didStatus === 'exists') {
      setShowDidCreatePrompt(false);
      return;
    }
    if (!authState.isVerified || authState.didStatus === 'checking') {
      return;
    }
    if (authState.didStatus === 'none') {
      setShowDidCreatePrompt(true);
    }
  }, [authState.isVerified, authState.didStatus]);

  // Warn guest users about data loss on refresh/close if they have an active conversation
  useEffect(() => {
    if (!isGuest || messages.length === 0) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have an active conversation in guest mode. Refreshing or closing this tab will lose your chat history. Connect a wallet to save it securely.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isGuest, messages.length]);

  // IntersectionObserver: only poll concurrency when widget is visible AND user has sent at least one message
  useEffect(() => {
    if (!widgetRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setWidgetVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(widgetRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToWalletConnect = useCallback(() => {
    document.getElementById('wallet-connect-region')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    });
  }, []);

  // Deduplicate messages: remove consecutive assistant messages with identical content
  const deduplicatedMessages = useMemo(() => {
    if (messages.length === 0) return [];

    const result = [messages[0]];

    for (let i = 1; i < messages.length; i++) {
      const msg = messages[i];
      const prevMsg = result[result.length - 1];

      // Skip if this is an assistant message with same content as previous assistant message
      if (msg.role === 'assistant' &&
          prevMsg?.role === 'assistant' &&
          msg.content === prevMsg.content) {
        continue;
      }

      result.push(msg);
    }

    return result;
  }, [messages]);

  const showAssistantTyping = useMemo(() => {
    if (!isSending && !isStreaming) return false;
    const last = deduplicatedMessages[deduplicatedMessages.length - 1];
    return !last || last.role !== 'assistant' || last.content.length === 0;
  }, [isSending, isStreaming, deduplicatedMessages]);

  const handleCreateDID = useCallback(async () => {
    if (!wallet) {
      showToast('error', 'Wallet not connected');
      return;
    }

    if (!signer) {
      showToast('error', 'Wallet not connected. Please connect your wallet and try again.');
      return;
    }

    if (isCreatingDid) return;

    setIsCreatingDid(true);
    try {
      // Re-check on-chain before minting — user may already have a token from a prior session.
      if (provider) {
        const onChain = await fetchOnChainDidStatus(provider, wallet.address);
        if (onChain.hasDid) {
          showToast('success', `Safe ID Token found (ID: ${onChain.tokenId})`);
          await checkDID({ skipVerifiedCheck: true });
          setShowDidCreatePrompt(false);
          return;
        }
      }

      showToast('info', 'Creating Safe ID Token...');

      const { tokenId } = await createMyDid(signer);

      const tokenIdStr = tokenId.toString();
      setDidCreatedTokenId(tokenIdStr);
      showToast('success', 'Safe ID Token created successfully.');

      await checkDID({ skipVerifiedCheck: true });
      setShowDidCreatePrompt(false);
    } catch (err: any) {
      console.error('Error creating DID:', err);
      const message = err.message || '';

      if (message.includes('already exists')) {
        showToast('error', 'A DID already exists for this wallet.');
      } else if (message.includes('switch to ' + SUPPORTED_NETWORK_NAME)) {
        showToast('error', `Please switch your wallet to ${SUPPORTED_NETWORK_NAME} and try again.`);
      } else if (message.includes('User denied') || message.includes('rejected') || err.code === 'ACTION_REJECTED') {
        showToast('warning', 'DID creation cancelled. You can create it later from My ID token.');
      } else if (message.includes('insufficient') || message.includes('needed for gas') || message.includes('gas')) {
        showToast('error', 'You do not have enough ETH for the network fee. Please add ETH to your wallet.');
      } else {
        showToast('error', message || 'Failed to create Safe ID Token');
      }
    } finally {
      setIsCreatingDid(false);
    }
  }, [wallet, signer, provider, checkDID, showToast, isCreatingDid]);

  // Show manual save button after AI response (user must click to save to DID)
  const showManualSaveButton = useCallback(() => {
    if (isGuest || !signer || !wallet) {
      return;
    }
    setTimeout(() => {
      setPendingDidSave({ visible: true, saveResult: null });
    }, SAVE_SESSION_BUTTON_DELAY_MS);
  }, [isGuest, signer, wallet]);

  // Handle manual save to DID - user clicks "Save session with ID token" button
  const handleSaveToDid = useCallback(async () => {
    if (!signer || !wallet || !encryptedKey) {
      showToast('error', 'Wallet not connected or encryption not ready');
      return;
    }

    setIsSavingToDid(true);
    void analytics.trackHistorySaveStarted().catch(() => {});

    try {
      // Save encrypted chat to backend first (off-chain storage)
      const updatedMessages = [...deduplicatedMessages];
      const saveResult = await saveChat(
        updatedMessages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
        }))
      );

      const contentHash = saveResult.contentHash;
      if (!contentHash) {
        throw new Error('No content hash received from backend');
      }

      // Off-chain save succeeded
      const isHybridAnchoring = saveResult.requiresDidUpdate && signer;

      if (isHybridAnchoring) {
        // Hybrid flow: anchor the content hash on-chain as integrity proof
        showToast('info', 'Anchoring chat integrity on-chain...');
        const metaBytes = keyMetadataBytesForDidUpdate(
          encryptedKey,
          saveResult.preserveEncryptedKeyMetadata,
        );

        if (metaBytes.length === 0) {
          throw new Error('Encrypted key metadata is empty');
        }

        // Update encrypted key metadata on-chain (preserve key for decryption)
        await updateChatReference(signer, contentHash, metaBytes);

        showToast('success', 'Session saved — chat integrity anchored on-chain!');
      } else {
        // Pure off-chain save: stored locally, no blockchain transaction needed
        showToast('success', 'Session saved (off-chain). You can anchor integrity later from My ID token.');
      }

      setPendingDidSave({ visible: false, saveResult: null });
      setChatIsSaved(true);
      // Onboarding coach: first session saved with an ID token → tour complete.
      markSaveCompleted();
      emitChatState({ saveConfirmed: true });
    } catch (err: any) {
      console.error('Error saving to DID:', err);
      if (err.code === 'ACTION_REJECTED') {
        showToast('warning', 'Save cancelled — chat is still stored locally. You can save later.');
      } else {
        showToast('error', err.message || 'Failed to save chat');
      }
    } finally {
      setIsSavingToDid(false);
    }
  }, [deduplicatedMessages, signer, wallet, encryptedKey, saveChat, showToast]);

  // Once the guest allowance is spent, show the conversion modal after streaming finishes.
  useEffect(() => {
    if (!isGuest || isStreaming) return;
    if (guestPromptCount >= guestPromptLimit) {
      setShowGuestLimitModal(true);
    }
  }, [isGuest, guestPromptCount, isStreaming, guestPromptLimit]);

  // Check concurrency status
  const checkConcurrencyStatus = useCallback(async (): Promise<boolean> => {
    if (!wallet?.address) return false;

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      headers['x-wallet-address'] = wallet.address;
      headers['x-chain-id'] = wallet.chainId.toString();

      const response = await apiFetch('/chat/concurrency', {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const inProgress = data.data.requestInProgress || false;
          setRequestInProgress((prev) => (prev === inProgress ? prev : inProgress));
          return inProgress;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking concurrency status:', error);
      // Don't show error to user, just log it
      return false;
    }
  }, [wallet?.address, wallet?.chainId]);

  // Poll concurrency status periodically when wallet is connected
  // Only poll when: widget is visible, user has sent at least one message, and symmetric key is ready
  useEffect(() => {
    if (wallet?.address && symmetricKey && !encryptionLoading && widgetVisible && hasSentMessage) {
      // Check immediately
      checkConcurrencyStatus();

      // Then poll every 5 seconds (avoid unnecessary re-renders)
      concurrencyCheckIntervalRef.current = setInterval(() => {
        checkConcurrencyStatus();
      }, 5000);

      return () => {
        if (concurrencyCheckIntervalRef.current) {
          clearInterval(concurrencyCheckIntervalRef.current);
        }
      };
    } else {
      // Reset requestInProgress when wallet disconnects or widget not visible
      setRequestInProgress(false);
      if (concurrencyCheckIntervalRef.current) {
        clearInterval(concurrencyCheckIntervalRef.current);
        concurrencyCheckIntervalRef.current = null;
      }
    }
  }, [wallet?.address, symmetricKey, encryptionLoading, checkConcurrencyStatus, widgetVisible, hasSentMessage]);

  // Load encrypted chat history when wallet session is verified and the symmetric key is ready.
  // Always replaces in-memory messages so a prior guest thread does not leak after verification.
  useEffect(() => {
    if (symmetricKey && !encryptionLoading && authState.isVerified) {
      setIsLoadingHistory(true);
      void analytics.trackHistoryRetrievalStarted().catch(() => {});
      loadChat()
        .then((loadedMessages) => {
          const raw = loadedMessages ?? [];

          // DEDUP: Remove consecutive duplicate assistant messages with identical content
          const deduped: any[] = [];
          for (let i = 0; i < raw.length; i++) {
            const msg = raw[i];
            const prev = deduped[deduped.length - 1];
            // Skip if this is an assistant message with same content as previous
            if (msg.role === 'assistant' && prev?.role === 'assistant' && msg.content === prev.content) {
              continue;
            }
            deduped.push(msg);
          }

          setMessages(
            deduped.map((msg: any) => ({
              id: msg.id || `msg-${Date.now()}-${Math.random()}`,
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
              encrypted: false,
            }))
          );
          // Loaded chat is considered "saved" (it came from backend)
          setChatIsSaved(raw.length > 0);
          setIsLoadingHistory(false);
          void analytics.trackHistoryRetrievalSucceeded().catch(() => {});
        })
        .catch((err) => {
          console.error('Error loading chat:', err);
          showToast('error', 'Failed to load chat history');
          setIsLoadingHistory(false);
        });
    } else if (!symmetricKey && !encryptionLoading) {
      setIsLoadingHistory(false);
    }
  }, [symmetricKey, encryptionLoading, loadChat, showToast, authState.isVerified]);

  // Scroll chat to latest message when history changes or while waiting for a reply
  useEffect(() => {
    const behavior: ScrollBehavior = isStreaming ? 'auto' : 'smooth';
    scrollToBottom(behavior);
  }, [messages, isStreaming, isSending, showAssistantTyping, scrollToBottom]);

  // Handle sending a message.
  // Guest mode: messages stay in React state only; API requests include mode: 'guest' (no server persistence).
  // Authenticated: wallet-derived encryption + saveChat + quota path.
  const handleSend = async (contentOverride?: string) => {
    const trimmed = (typeof contentOverride === 'string' ? contentOverride : input).trim();
    if (!trimmed || isStreaming || isSending || trimmed.length > MAX_MESSAGE_LENGTH || requestInProgress) return;

    // Crisis detection: check message before sending (client-side only)
    checkMessage(trimmed);
    // Note: crisis detection is non-blocking - message still sends

    // Authenticated users: require encryption to be ready
    if (!isGuest) {
      if (encryptionError) {
        showToast('error', 'Encryption error. Please refresh the page.');
        return;
      }
      if (!symmetricKey || encryptionLoading) {
        return;
      }
    }

    if (isGuest && guestPromptCount >= guestPromptLimit) {
      setShowGuestLimitModal(true);
      return;
    }

    if (isOffline) {
      showToast('error', 'Cannot send messages while offline. Please check your connection.');
      return;
    }

    setIsSending(true);
    setSendPhase('checking');

    let streamFinalized = false;
    try {
      // Check concurrency status one more time before sending (wallet / DID path only)
      const isInProgress = await checkConcurrencyStatus();
      if (isInProgress) {
        showToast('error', 'Please wait for the chatbot to respond before sending another message.');
        return;
      }

      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
        encrypted: false,
      };

      if (isGuest) {
        setGuestPromptCount((c) => {
          const next = c + 1;
          writeGuestPromptCount(next);
          return next;
        });
      }

      setMessages((prev) => [...prev, userMessage]);
      recordFirstMessageSent();
      // v2 funnel: every message (first + subsequent) contributes to engagement;
      // second-and-later also mark qualified activation.
      void analytics.trackMessageSubmitted(messages.length > 0)

      // Don't save intermediate messages - only save when user clicks "Save history chat on DID"
      // Mark chat as unsaved since user is adding new content
      setChatIsSaved(false);

      setInput('');
      setIsStreaming(true);
      setSendPhase('sending');

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      // Messages are already decrypted in state, just format for API
      const decryptedMessages = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content: msg.content, // Already decrypted
        timestamp: msg.timestamp,
      }));

      // Prepare headers with wallet authentication if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Also add wallet headers for quota checking
      if (wallet) {
        headers['x-wallet-address'] = wallet.address;
        headers['x-chain-id'] = wallet.chainId.toString();
      }

      // Correlate anonymous visitor/session so server-confirmed analytics events
      // (ai_response_*, guest_session_started) join the client funnel.
      headers['x-visitor-id'] = analytics.getVisitorId();
      headers['x-session-id'] = analytics.getSessionId();

      // Build URL with withContext query parameter if needed
      const chatUrl = new URL(apiUrl('/chat/completions'), window.location.origin);
      if (withContext) {
        chatUrl.searchParams.set('withContext', 'true');
      }

      // Stream response from API
      const response = await apiFetch(chatUrl.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: decryptedMessages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          stream: true,
          mode: isGuest ? 'guest' : 'authenticated',
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }

        // Check if it's a quota exceeded error
        if (response.status === 429 && errorData.error === 'QUOTA_EXCEEDED') {
          openSubscriptionTerminal(true);
          setIsStreaming(false);
          return;
        }

        // Server-side guest quota (per signed device cookie / IP hard cap): sync the
        // client counter to the reported value so refreshers see the real state and
        // open the guest modal instead of a generic error toast.
        if (response.status === 429 && errorData.error === 'GUEST_QUOTA_EXCEEDED') {
          const dailyUsed =
            typeof errorData?.quota?.dailyUsed === 'number'
              ? errorData.quota.dailyUsed
              : guestPromptLimit;
          const dailyLimit =
            typeof errorData?.quota?.dailyLimit === 'number'
              ? errorData.quota.dailyLimit
              : guestPromptLimit;
          const capped = Math.max(dailyUsed, dailyLimit);
          setGuestPromptCount(capped);
          writeGuestPromptCount(capped);
          setShowGuestLimitModal(true);
          setIsStreaming(false);
          return;
        }

        // Burst sending tripped the chat rate limiter (authenticated or guest): the
        // server already enforced the 429, so mirror a clear "retry later" message
        // instead of a generic error, and leave the UI ready to retry.
        if (
          response.status === 429 &&
          (errorData.error === 'RATE_LIMIT_EXCEEDED' ||
            errorData.error === 'GUEST_RATE_LIMIT_EXCEEDED')
        ) {
          setSendPhase('idle');
          setIsStreaming(false);
          showToast(
            'error',
            errorData.error === 'GUEST_RATE_LIMIT_EXCEEDED'
              ? 'Too many guest messages right now. Please wait a moment, or sign in with your wallet to keep chatting.'
              : 'You\'re sending messages too quickly. Please wait a moment and try again.',
          );
          return;
        }

        // Health-data consent missing or withdrawn: pop open the cookie
        // preferences so the user can grant GDPR Art. 9 consent, then surface
        // the message below.
        if (
          response.status === 403 &&
          (errorData.error === 'CONSENT_REQUIRED' || errorData.code === 'CONSENT_REQUIRED')
        ) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('open-cookie-manager'));
          }
        }

        const errorMsg = getActionableErrorMessage(null, {
          status: response.status,
          code: errorData.error || errorData.code,
          originalMessage: errorData.message,
        });
        throw new Error(errorMsg);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';
      let currentEvent = '';
      let assistantMessage: Message = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };

      const persistAfterStream = async () => {
        if (streamFinalized) return;
        streamFinalized = true;
        setIsStreaming(false);
        setSendPhase('idle');
        if (assistantMessage.content.trim().length > 0) {
          recordFirstReplyReceived();
          void analytics.trackAiResponseReceived().catch(() => {})
          // Onboarding coach: first Sage answer is in (step 4).
          emitChatState({ firstReplyReceived: true });
        }
        // CRITICAL FIX: Message is ALREADY in state from streaming updates.
        // Do NOT add/update here - removing this setMessages call prevents duplicates.
        // Just show the save button.

        // Show manual save button after 3 seconds (for authenticated users with DID)
        if (!isGuest && signer && wallet && encryptedKey) {
          showManualSaveButton();
        }
      };

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;
        if (sendPhase !== 'streaming') setSendPhase('streaming');

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const raw = line.substring(6).trim();
              if (raw === '[DONE]') {
                await persistAfterStream();
                continue;
              }
              const data = JSON.parse(raw);

              // OpenAI-compatible SSE (chat.completion.chunk): no "event:" lines
              // Check for delta.content ONLY when there's no explicit event type (default OpenAI format)
              const delta = data?.choices?.[0]?.delta as { content?: string } | undefined;
              if (delta?.content && !currentEvent) {
                assistantMessage.content += delta.content;
                setMessages((prev) => {
                  // DEFENSIVE: Filter out any duplicate assistant messages with same ID
                  const filtered = prev.filter((m, idx) => {
                    if (m.role === 'assistant' && m.id === assistantMessage.id) {
                      // Keep only the LAST occurrence of our streaming message
                      const laterIndex = prev.findIndex((m2, idx2) =>
                        idx2 > idx && m2.role === 'assistant' && m2.id === assistantMessage.id
                      );
                      return laterIndex === -1;
                    }
                    return true;
                  });

                  // Now find if our message exists in the filtered array
                  const existingIndex = filtered.findIndex(
                    (m) => m.id === assistantMessage.id && m.role === 'assistant'
                  );
                  if (existingIndex !== -1) {
                    const updated = [...filtered];
                    updated[existingIndex] = { ...updated[existingIndex], content: assistantMessage.content };
                    return updated;
                  }
                  return [...filtered, assistantMessage];
                });
              }
              // Don't call persistAfterStream on finish_reason - it will be called on [DONE] or event:done

              if (currentEvent === 'token') {
                // Legacy event format: event: token with data.token
                assistantMessage.content += data.token;
                setMessages((prev) => {
                  // DEFENSIVE: Filter out any duplicate assistant messages with same ID
                  const filtered = prev.filter((m, idx) => {
                    if (m.role === 'assistant' && m.id === assistantMessage.id) {
                      // Keep only the LAST occurrence of our streaming message
                      const laterIndex = prev.findIndex((m2, idx2) =>
                        idx2 > idx && m2.role === 'assistant' && m2.id === assistantMessage.id
                      );
                      return laterIndex === -1;
                    }
                    return true;
                  });

                  // Now find if our message exists in the filtered array
                  const existingIndex = filtered.findIndex(
                    (m) => m.id === assistantMessage.id && m.role === 'assistant'
                  );
                  if (existingIndex !== -1) {
                    const updated = [...filtered];
                    updated[existingIndex] = { ...updated[existingIndex], content: assistantMessage.content };
                    return updated;
                  }
                  return [...filtered, assistantMessage];
                });
              } else if (currentEvent === 'metadata') {
                // Local mock stream advertises itself via metadata.
                const model = String(data?.model || '');
                if (model === 'local-mock') {
                  setIsMockMode(true);
                }
              } else if (currentEvent === 'done') {
                await persistAfterStream();
              } else if (currentEvent === 'error') {
                // Check if it's a quota exceeded error
                if (data.error === 'QUOTA_EXCEEDED') {
                  openSubscriptionTerminal(true);
                  setIsStreaming(false);
                  return;
                }
                // Chat rate limit surfaced mid-stream: tell the user to retry later.
                if (
                  data.error === 'RATE_LIMIT_EXCEEDED' ||
                  data.error === 'GUEST_RATE_LIMIT_EXCEEDED'
                ) {
                  setIsStreaming(false);
                  showToast(
                    'error',
                    'You\'re sending messages too quickly. Please wait a moment and try again.',
                  );
                  return;
                }
                throw new Error(data.message || 'Streaming error');
              }
            } catch (error) {
              console.error('Error parsing SSE data:', error);
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        const errorMsg = error instanceof Error
          ? error.message
          : getActionableErrorMessage(error);
        
        setSendPhase('error');
        showToast('error', errorMsg);
        
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-${Date.now()}-error`,
            role: 'assistant',
            content: `I encountered an error: ${errorMsg}. Please try again.`,
            timestamp: new Date(),
            encrypted: false,
          },
        ]);
      }
    } finally {
      if (!streamFinalized) {
        setIsStreaming(false);
      }
      setIsSending(false);
      setSendPhase('idle');
    }
  };

  // Handle cancel streaming
  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  const guestAtLimit = isGuest && guestPromptCount >= guestPromptLimit;
  const guestOneLeft = isGuest && guestPromptCount === guestPromptLimit - 1;
  const freeUserAtQuota = !isGuest && !!quotaStatus && subscription?.plan === 'free' && !quotaStatus.canUse;

  return (
    <>
      <GuestModeLimitModal
        open={showGuestLimitModal}
        dailyLimit={guestPromptLimit}
        onClose={() => setShowGuestLimitModal(false)}
        onScrollToWallet={scrollToWalletConnect}
      />
      {/* DID Creation Prompt Modal */}
      {showDidCreatePrompt && authState.isVerified && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-neutral-200 dark:border-white/10">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-heading">Create Your Safe ID Token</h3>
              {shouldShowOnboarding() && (
                <span className="shrink-0 text-[11px] font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-full px-2 py-0.5">
                  Step 3 of 5
                </span>
              )}
            </div>
            <div className="space-y-4">
              <p className="text-sm text-body">
                Your wallet is verified! Now create your <strong>ID token</strong> to:
              </p>
              <ul className="space-y-2 text-sm text-body">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Save chat history permanently to your wallet</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Access your chats from any device</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Keep your data secure and private</span>
                </li>
              </ul>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  Creating the token is free — you only pay the transaction fees. Make sure you have a few euros&apos;
                  worth of ETH in your wallet to pay for the transaction. You&apos;re ready as soon as you see{' '}
                  <strong>&quot;Signed In&quot;</strong> at the top of the page.
                </p>
              </div>
              <div className="pt-2">
                <button
                  onClick={handleCreateDID}
                  disabled={isCreatingDid}
                  aria-busy={isCreatingDid}
                  data-onboarding-anchor="create-did"
                  className="w-full px-4 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold rounded-lg hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreatingDid ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating Safe ID Token…</span>
                    </>
                  ) : (
                    'Create Safe ID Token'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={widgetRef} className="flex flex-col min-h-0 overflow-hidden max-h-[min(600px,calc(100dvh-14.5rem))] bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-white/10 shadow-sm">
      {/* Header */}
      <div className="shrink-0 px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-white/20 rounded-t-lg space-y-2">
        {isMockMode && (
          <p
            className="text-xs font-medium text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-center"
            role="status"
          >
            Chat is running in local mock mode (LLM upstream not configured). To enable real responses, set
            {' '}<code className="font-mono">SCALEWAY_API_KEY</code> (set <code className="font-mono">USE_SCALEWAY_DIRECT=1</code> on the API).
          </p>
        )}
        {isGuest ? (
          <p
            className="text-xs font-medium text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-center"
            role="status"
          >
            No account needed — you have {guestPromptLimit} free messages a day. This chat lives in this tab only
            (refresh clears it); connect a wallet whenever you want to keep it.
          </p>
        ) : (
          <p className="text-xs text-body opacity-60 text-center">
            All the exchanged data will be encrypted and stored in your personal wallet. Nobody except you
            will have access to it
          </p>
        )}
      </div>

      {/* Messages: hug content until the viewport cap, then scroll inside the thread */}
      <div className="min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 bg-neutral-50 dark:bg-neutral-950">
        {isLoadingHistory ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="py-1 px-2">
            <div className="flex flex-col items-center justify-center py-1 px-4">
              <img
                src="/Sage.png"
                alt=""
                width={48}
                height={48}
                className="w-12 h-12 object-contain"
              />
            </div>
            {FIRST_MESSAGE_SUGGESTIONS.length > 0 ? (
              <div className="max-w-md mx-auto mt-3 space-y-2">
                {FIRST_MESSAGE_SUGGESTIONS.map((line) => (
                  <button
                    key={line}
                    type="button"
                    disabled={guestAtLimit || freeUserAtQuota || isOffline || isStreaming}
                    onClick={() => void handleSend(line)}
                    className="w-full text-left text-sm px-4 py-3 rounded-xl border border-primary-200 dark:border-primary-800 bg-white/80 dark:bg-neutral-900/60 text-heading hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {line}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          deduplicatedMessages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
            />
          ))
        )}

        {showAssistantTyping && (
          <div className="flex justify-start" role="status" aria-live="polite" aria-label="Assistant is responding">
            <TypingDots className="py-2" />
          </div>
        )}

        {/* Manual Save / Create DID Button - appears 3 seconds after AI response */}
        {pendingDidSave.visible && pendingDidSave.saveResult === null && (
          <div className="mt-4 flex flex-col items-center gap-2">
            {authState.didStatus === 'exists' || didCreatedTokenId ? (
              <button
                onClick={handleSaveToDid}
                disabled={isSavingToDid || isCreatingDid}
                data-onboarding-anchor="save-session"
                aria-busy={isSavingToDid}
                className="no-button-shimmer flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                title="Save this conversation encrypted off-chain, anchored with your Safe ID Token on-chain"
              >
                {isSavingToDid ? (
                  <>
                    <Loader2 className="relative z-10 w-4 h-4 animate-spin" />
                    <span className="relative z-10">Saving chat with ID token…</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="relative z-10 w-4 h-4" />
                    <span className="relative z-10">Save session with ID token</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleCreateDID}
                disabled={isCreatingDid || isSavingToDid}
                aria-busy={isCreatingDid}
                data-onboarding-anchor="create-did"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                title="Create your Safe ID Token first so this session can be saved and anchored on-chain"
              >
                {isCreatingDid ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating Safe ID Token…</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Create a DID to save session</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Save Success Message - replaces button after successful save */}
        {pendingDidSave.visible && !isSavingToDid && pendingDidSave.saveResult === 'success' && (
          <div className="mt-4 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300 font-medium">
              <CheckCircle className="w-4 h-4" />
              <span>Chat saved with ID token</span>
            </div>
          </div>
        )}

        {/* Save Cancelled Message */}
        {pendingDidSave.visible && !isSavingToDid && pendingDidSave.saveResult === 'cancelled' && (
          <div className="mt-4 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
              <AlertCircle className="w-4 h-4" />
              <span>Save cancelled - you can save later from My ID token</span>
            </div>
          </div>
        )}

        {/* Save Error Message */}
        {pendingDidSave.visible && !isSavingToDid && pendingDidSave.saveResult === 'error' && (
          <div className="mt-4 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4" />
              <span>Failed to save - please try again</span>
            </div>
          </div>
        )}

        {/* Unsaved Changes Indicator */}
        {(() => {
          const lastMsg = deduplicatedMessages[deduplicatedMessages.length - 1];
          const isLastError = lastMsg?.role === 'assistant' && lastMsg.content.startsWith('I encountered an error:');
          if (isLastError) return null;
          if (!pendingDidSave.visible && messages.length > 0 && !chatIsSaved && !isGuest && authState.isVerified) {
            return (
              <div className="mt-4 flex justify-center">
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                  <AlertCircle className="w-4 h-4" />
                  <span>Unsaved changes - {authState.didStatus === 'exists' ? 'click "Save session with ID token" when response appears' : 'create a DID to save this session'}</span>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-gray-200 dark:border-white/20 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-b-lg">
        {/* Offline Indicator */}
        {isOffline && (
          <div className="mb-2 flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
            <WifiOff className="w-4 h-4" />
            <span>Offline - Messages cannot be sent</span>
          </div>
        )}

        {/* Quota Warning Banner — only for authenticated non-guest users */}
        {!isGuest && quotaStatus && subscription?.status === 'active' && subscription.plan !== 'free' && (
          <>
            {quotaStatus.dailyLimit > 0 && !quotaStatus.canUse && (
              <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs font-medium text-red-700 dark:text-red-300" role="alert">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Daily limit reached — upgrade to keep going.</span>
                <button
                  type="button"
                  onClick={() => openSubscriptionTerminal(true)}
                  className="ml-auto underline text-primary-600 dark:text-primary-400 hover:text-primary-700 whitespace-nowrap"
                >
                  Upgrade
                </button>
              </div>
            )}
            {quotaStatus.dailyLimit > 0 && quotaStatus.canUse && getUsagePercentage({ used: quotaStatus.dailyUsed, limit: quotaStatus.dailyLimit, remaining: quotaStatus.dailyLimit - quotaStatus.dailyUsed }) >= 90 && (
              <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>
                  {quotaStatus.dailyLimit - quotaStatus.dailyUsed} prompts remaining today ({quotaStatus.dailyUsed}/{quotaStatus.dailyLimit}).
                </span>
                <button
                  type="button"
                  onClick={() => openSubscriptionTerminal(true)}
                  className="ml-auto underline text-primary-600 dark:text-primary-400 hover:text-primary-700 whitespace-nowrap"
                >
                  Upgrade
                </button>
              </div>
            )}
            {quotaStatus.dailyLimit > 0 && quotaStatus.canUse && getUsagePercentage({ used: quotaStatus.dailyUsed, limit: quotaStatus.dailyLimit, remaining: quotaStatus.dailyLimit - quotaStatus.dailyUsed }) >= 80 && getUsagePercentage({ used: quotaStatus.dailyUsed, limit: quotaStatus.dailyLimit, remaining: quotaStatus.dailyLimit - quotaStatus.dailyUsed }) < 90 && (
              <div className="mb-2 flex items-center gap-1.5 text-xs text-body opacity-70">
                <span>{quotaStatus.dailyLimit - quotaStatus.dailyUsed} prompts left today</span>
                <span className="opacity-50">·</span>
                <span>{quotaStatus.dailyUsed}/{quotaStatus.dailyLimit}</span>
              </div>
            )}
          </>
        )}
        {guestOneLeft && !guestAtLimit && (
          <p className="mb-2 text-xs text-amber-700 dark:text-amber-300" role="status">
            One free message left today. Connect a wallet to keep your history and keep chatting.
          </p>
        )}
        {guestAtLimit && (
          <p className="mb-2 text-xs text-amber-800 dark:text-amber-200 font-medium" role="alert">
            You&apos;ve used all {guestPromptLimit} free messages for today — they refresh tomorrow.
            <button
              type="button"
              onClick={() => setShowGuestLimitModal(true)}
              className="ml-2 underline text-primary-600 dark:text-primary-400"
            >
              Save this conversation
            </button>
          </p>
        )}

        {/* Quota warning banner for free users */}
        {freeUserAtQuota && (
          <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs font-medium text-red-700 dark:text-red-300" role="alert">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Daily limit reached.</span>
            <button
              type="button"
              onClick={() => openSubscriptionTerminal(true)}
              className="ml-auto underline text-primary-600 dark:text-primary-400 hover:text-primary-700 whitespace-nowrap"
            >
              Upgrade
            </button>
          </div>
        )}

        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={input}
              data-onboarding-anchor="chat-input"
              onChange={(e) => {
                if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                  setInput(e.target.value);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={
                isGuest
                  ? 'Say it in your own words...'
                  : 'Type your message...'
              }
              disabled={
                isStreaming ||
                isSending ||
                (!isGuest && (!symmetricKey || encryptionLoading)) ||
                isOffline ||
                requestInProgress ||
                guestAtLimit ||
                freeUserAtQuota
              }
              maxLength={MAX_MESSAGE_LENGTH}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-neutral-950 text-heading placeholder:text-body placeholder:opacity-50"
            />
          </div>
          {isStreaming ? (
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              aria-label="Cancel streaming"
            >
              Cancel
            </button>
          ) : freeUserAtQuota ? (
            <button
              onClick={() => void openSubscriptionTerminal(true)}
              className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary-500/25 transform hover:-translate-y-0.5 active:scale-95 flex items-center justify-center min-h-[44px] min-w-[44px]"
              aria-label="Upgrade to send messages"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => void handleSend()}
              disabled={
                !input.trim() ||
                isSending ||
                (!isGuest && (!symmetricKey || encryptionLoading)) ||
                isOffline ||
                input.length > MAX_MESSAGE_LENGTH ||
                requestInProgress ||
                isStreaming ||
                guestAtLimit
              }
              className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary-500/25 transform hover:-translate-y-0.5 active:scale-95 flex items-center justify-center min-h-[44px] min-w-[44px]"
              aria-label="Send message"
              title={requestInProgress ? "Please wait for the chatbot to respond before sending another message" : ""}
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="sr-only" role="status" aria-live="polite">
          {!isGuest && encryptionLoading
            ? 'Preparing secure encryption'
            : showAssistantTyping
              ? 'Assistant is responding'
              : sendPhase === 'saving'
                ? 'Saving securely'
                : ''}
        </p>
        {!isGuest && encryptionLoading && (
          <p className="text-xs text-body opacity-75 mt-2 flex items-center gap-1">
            <span className="animate-spin">⟳</span>
            Initializing encryption...
          </p>
        )}
        {!isGuest && encryptionError && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">{encryptionError}</p>
        )}
      </div>
      </div>
      <ToastContainer />

      {/* Safety Net Modal - Crisis Support */}
      <SafetyModal
        isOpen={showSafetyModal}
        onClose={() => {
          dismissModal();
          setShowSafetyModal(false);
        }}
        onOptIn={async (data) => {
          await registerSafetyNet(data);
        }}
        detectedLanguage={detectedLanguage}
        hotline={crisisHotline}
      />
    </>
  );
}

// Message Bubble Component - no edit/delete functionality
const MessageBubble = memo(function MessageBubble({
  message,
}: {
  message: Message;
}) {
  const isUser = message.role === 'user';

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 ${
          isUser
            ? 'bg-indigo-600 text-white'
            : 'bg-gray-50 dark:bg-gray-800/50 text-heading border border-gray-200 dark:border-white/20'
        }`}
      >
        <p className={`text-sm whitespace-pre-wrap ${isUser ? 'text-white' : 'text-heading'}`}>{message.content}</p>
        {message.encrypted ? (
          <p className={`text-xs mt-2 ${isUser ? 'text-indigo-100' : 'text-body opacity-75'}`}>
            <span aria-label="Encrypted">🔒</span>
          </p>
        ) : null}
      </div>
    </div>
  );
});

